import { useEffect, useRef } from 'react';
import { auditApi } from '../api/client';

const HEARTBEAT_INTERVAL_MS = 15_000;

/**
 * Tracks engagement time on the document at `path`. Lifecycle:
 *  - Mount or path change -> POST /audit/heartbeat/start, store token.
 *  - Every 15s while the tab is visible -> POST /audit/heartbeat with the
 *    elapsed wall-clock time since the previous heartbeat (clamped to 15s).
 *  - Unmount, path change, or browser unload -> close the engagement and flush
 *    the trailing fragment via sendBeacon.
 *
 * Designed to be safe under React 18 StrictMode (which mounts effects twice
 * in development) -- the cleanup closes the token created during the same
 * effect run, so we never leak engagement rows.
 */
export function useDocumentEngagement(path: string | null): void {
  const tokenRef = useRef<string | null>(null);
  const lastBeatRef = useRef<number>(0);

  useEffect(() => {
    if (!path) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let localToken: string | null = null;
    lastBeatRef.current = Date.now();

    auditApi
      .startHeartbeat(path)
      .then(({ token }) => {
        if (cancelled) {
          // Component unmounted before the start round-trip resolved. Close
          // the engagement immediately so the row is not left dangling.
          void auditApi.closeHeartbeat(token, 0);
          return;
        }
        localToken = token;
        tokenRef.current = token;
        lastBeatRef.current = Date.now();
        intervalId = setInterval(tick, HEARTBEAT_INTERVAL_MS);
      })
      .catch(() => undefined); // audit failures must not affect the viewer

    function tick(): void {
      if (!localToken) return;
      // Only count time when the tab is actively visible. The clamp matches
      // the server-side guard so a long sleep does not produce a giant delta.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        lastBeatRef.current = Date.now();
        return;
      }
      const now = Date.now();
      const delta = Math.min(now - lastBeatRef.current, HEARTBEAT_INTERVAL_MS);
      lastBeatRef.current = now;
      if (delta <= 0) return;
      void auditApi.heartbeat(localToken, delta).catch(() => undefined);
    }

    function handleBeforeUnload(): void {
      if (!localToken) return;
      const now = Date.now();
      const delta = Math.min(now - lastBeatRef.current, HEARTBEAT_INTERVAL_MS);
      void auditApi.closeHeartbeat(localToken, Math.max(delta, 0));
      localToken = null;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (intervalId) clearInterval(intervalId);
      const token = localToken;
      if (token) {
        const now = Date.now();
        const delta = Math.min(now - lastBeatRef.current, HEARTBEAT_INTERVAL_MS);
        void auditApi.closeHeartbeat(token, Math.max(delta, 0));
      }
      localToken = null;
      tokenRef.current = null;
    };
  }, [path]);
}
